import { Checkbox, Dropdown, IDropdownOption, PrimaryButton, Stack, TextField } from "@fluentui/react"
import { useState } from "react"

interface IContactForm {
	name: string
	email: string
	memberId: string
	role: string
	subCategory: string
	message: string

	// Provider Outreach specific fields
	providerName: string
	providerPhone: string
	practiceName: string
	providerAddress: string
	isPatient: boolean
	allowNameUsage: boolean
	memberName: string
	memberCompany: string
	memberEmail: string
	memberPhone: string
	additionalInfo: string
}

const roleOptions: IDropdownOption[] = [
	{ key: "member", text: "Member" },
	{ key: "provider", text: "Provider" },
	{ key: "employer", text: "Employer/HR" },
	{ key: "other", text: "Other" }
]

const subCategoryMap: Record<string, IDropdownOption[]> = {
	member: [
		{ key: "claims_account", text: "Claims or Account Question" },
		{ key: "provider_outreach", text: "Provider Outreach" },
		{ key: "prescription_issue", text: "Prescription Issue" },
		{ key: "member_other", text: "Something else" }
	],
	provider: [
		{ key: "eligibility_check", text: "Eligibility Check" },
		{ key: "claim_question", text: "Claim Question" },
		{ key: "provider_other", text: "Something else" }
	],
	employer: [
		{ key: "employee_issue", text: "Employee Issue" },
		{ key: "billing_question", text: "Billing Question" },
		{ key: "enrollment_change", text: "Enrollment Change" },
		{ key: "employee_other", text: "Something else" }
	],
	other: [
		{ key: "request_quote", text: "Request a Quote" },
		{ key: "general_question", text: "General Question for ACMG" },
		{ key: "other", text: "Something else" }
	]
}

const initialFormState: IContactForm = {
	name: "",
	email: "",
	memberId: "",
	role: "",
	subCategory: "",
	message: "",
	providerName: "",
	providerPhone: "",
	practiceName: "",
	providerAddress: "",
	isPatient: false,
	allowNameUsage: false,
	memberName: "",
	memberCompany: "",
	memberEmail: "",
	memberPhone: "",
	additionalInfo: ""
}

export default function ContactUsForm() {
	const [form, setForm] = useState<IContactForm>(initialFormState)

	const handleChange = (field: keyof IContactForm, value: string | boolean) =>
		setForm(prev => {
			const updated = { ...prev, [field]: value }

			if (field === "role" && value !== prev.role) {
				updated.subCategory = ""
				updated.memberId = ""
			}

			// Sync base inputs with provider outreach inputs
			if (field === "name" && typeof value === "string") {
				updated.memberName = value
			}
			if (field === "email" && typeof value === "string") {
				updated.memberEmail = value
			}

			return updated
		})

	const renderDynamicFields = () => {
		const { role, subCategory } = form

		// Provider Outreach
		if (role === "member" && subCategory === "provider_outreach") {
			return (
				<Stack tokens={{ childrenGap: 12 }}>
					<Stack horizontal tokens={{ childrenGap: 12 }}>
						<Stack.Item grow={1}>
							<TextField label="Provider Name" required value={form.providerName} onChange={(_, newValue) => handleChange("providerName", newValue || "")} />
						</Stack.Item>
						<Stack.Item grow={1}>
							<TextField label="Provider's Phone" required value={form.providerPhone} onChange={(_, newValue) => handleChange("providerPhone", newValue || "")} />
						</Stack.Item>
					</Stack>
					<TextField label="Practice Name" value={form.practiceName} onChange={(_, newValue) => handleChange("practiceName", newValue || "")} />
					<TextField label="Provider's Address" value={form.providerAddress} onChange={(_, newValue) => handleChange("providerAddress", newValue || "")} />

					<Checkbox label="I am currently a patient of the above provider" checked={form.isPatient} onChange={(_, checked) => handleChange("isPatient", !!checked)} />
					<Checkbox
						label="My name may be used and/or referred to during the BSVN provider recruitment outreach process conducted by ACMG"
						checked={form.allowNameUsage}
						onChange={(_, checked) => handleChange("allowNameUsage", !!checked)}
					/>

					<Stack horizontal tokens={{ childrenGap: 12 }}>
						<Stack.Item grow={1}>
							<TextField label="Member Name" required value={form.memberName || form.name} onChange={(_, newValue) => handleChange("memberName", newValue || "")} />
						</Stack.Item>
						<Stack.Item grow={1}>
							<TextField label="Member Company" required value={form.memberCompany} onChange={(_, newValue) => handleChange("memberCompany", newValue || "")} />
						</Stack.Item>
					</Stack>
					<Stack horizontal tokens={{ childrenGap: 12 }}>
						<Stack.Item grow={1}>
							<TextField label="Member Email" required value={form.memberEmail || form.email} onChange={(_, newValue) => handleChange("memberEmail", newValue || "")} />
						</Stack.Item>
						<Stack.Item grow={1}>
							<TextField label="Member Phone" required value={form.memberPhone} onChange={(_, newValue) => handleChange("memberPhone", newValue || "")} />
						</Stack.Item>
					</Stack>
					<TextField label="Additional Information" multiline rows={2} value={form.additionalInfo} onChange={(_, newValue) => handleChange("additionalInfo", newValue || "")} />
				</Stack>
			)
		}

		// Example
		if (role === "member" && subCategory === "prescription_issue") {
			return (
				<Stack tokens={{ childrenGap: 12 }}>
					<TextField label="Medication Name" required />
					<TextField label="Pharmacy Name / Location" />
					<TextField multiline rows={4} label="Describe the issue" value={form.message} onChange={(_, newValue) => handleChange("message", newValue || "")} />
				</Stack>
			)
		}

		// Example
		if (role === "provider" && subCategory === "eligibility_check") {
			return (
				<Stack tokens={{ childrenGap: 12 }}>
					<TextField label="Provider NPI" required />
					<TextField label="Patient Date of Birth" placeholder="MM/DD/YYYY" required />
					<TextField multiline rows={3} label="Details or Notes" value={form.message} onChange={(_, newValue) => handleChange("message", newValue || "")} />
				</Stack>
			)
		}

		// Base Questions
		return (
			<Stack tokens={{ childrenGap: 12 }}>
				<TextField label="Full Name" required value={form.name} onChange={(_, newValue) => handleChange("name", newValue || "")} />
				<TextField label="Email Address" required value={form.email} onChange={(_, newValue) => handleChange("email", newValue || "")} />
				<TextField multiline rows={4} label="Message" value={form.message} onChange={(_, newValue) => handleChange("message", newValue || "")} />
			</Stack>
		)
	}

	return (
		<Stack tokens={{ childrenGap: 20 }} styles={{ root: { padding: 20, maxWidth: 550 } }}>
			<h3 style={{ margin: 0, fontSize: "24px", textAlign: "center" }}>ACMG Online Contact Portal</h3>
			<form
				onSubmit={e => {
					e.preventDefault()
					alert(`Submitting: ${JSON.stringify(form, null, 2)}`)
				}}>
				<Stack tokens={{ childrenGap: 14 }}>
					<Dropdown label="Identify who you are" required selectedKey={form.role} options={roleOptions} onChange={(_, option) => handleChange("role", (option?.key as string) || "")} />

					{form.role === "member" && (
						<TextField label="Member ID" placeholder="e.g. ACM-1234567" value={form.memberId} onChange={(_, newValue) => handleChange("memberId", newValue || "")} />
					)}

					{form.role && subCategoryMap[form.role] && (
						<Dropdown
							label="Reason for Inquiry"
							required
							selectedKey={form.subCategory}
							options={subCategoryMap[form.role]}
							onChange={(_, option) => handleChange("subCategory", (option?.key as string) || "")}
						/>
					)}

					{form.subCategory && renderDynamicFields()}

					<PrimaryButton type="submit" text="Submit Request" styles={{ root: { marginTop: 10 } }} />
				</Stack>
			</form>
		</Stack>
	)
}
